/**
 * Video Export Service using Queue System
 */

class VideoExportService {
  constructor() {
    this.baseUrl = (process.env.REACT_APP_API_BASE || '') + '/api/v1';
    this.activeJobs = new Map(); // jobId -> job data
  }

  isApiSuccess(data) {
    return Boolean(data?.success || (typeof data?.status === 'string' && data.status.toLowerCase() === 'success'));
  }

  /**
   * Start video export by adding to queue
   * @param {Object} template_content - The template content to render
   * @param {string} user_id - User ID
   * @param {string} format - Video format (default: mp4)
   * @param {string} template_id - Template ID for identification (optional)
   * @param {string} sample_type - Sample type (default: sample)
   * @param {Object} exportParams - Optional export parameters {req_width, req_height, req_fps}
   * @returns {Promise<{success: boolean, queueId: number}>}
   */
  async startVideoExport(template_content, user_id = 'default-user', format = 'mp4', template_id = null, sample_type = 'sample', exportParams = {}) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for job creation
      
      const requestBody = {
        user_id: user_id,
        template_content: template_content,
        format: format,
        template_id: template_id,
        sample_type: sample_type
      }
      
      // Add optional export parameters if provided
      if (exportParams.req_width) {
        requestBody.req_width = exportParams.req_width
      }
      if (exportParams.req_height) {
        requestBody.req_height = exportParams.req_height
      }
      if (exportParams.req_fps) {
        requestBody.req_fps = exportParams.req_fps
      }
      
      const response = await fetch(`${this.baseUrl}/queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (this.isApiSuccess(data)) {
        // Store job info
        this.activeJobs.set(data.queue_id, {
          id: data.queue_id,
          status: 'pending',
          progress: 0,
          startTime: Date.now()
        });

        return {
          success: true,
          queueId: data.queue_id,
          message: data.message
        };
      } else {
        throw new Error(data.message || 'Failed to start video export');
      }
    } catch (error) {
      console.error('Error starting video export:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get job status by queue ID
   * @param {number} queueId - Queue job ID
   * @returns {Promise<Object>} Job status
   */
  async getJobStatus(queueId) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for individual requests
      
      const response = await fetch(`${this.baseUrl}/queue/${queueId}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (this.isApiSuccess(data)) {
        // Update local cache
        this.activeJobs.set(queueId, {
          ...this.activeJobs.get(queueId),
          ...data.job
        });

        return data.job;
      } else {
        throw new Error('Failed to get job status');
      }
    } catch (error) {
      console.error('Error getting job status:', error);
      return {
        id: queueId,
        status: 'error',
        error_message: error.message
      };
    }
  }

  /**
   * Poll job status until completion
   * @param {number} queueId - Queue job ID
   * @param {Function} onProgress - Progress callback (progress, status, job)
   * @param {number} pollInterval - Poll interval in ms (default: 1000)
   * @returns {Promise<Object>} Final job result
   */
  async pollJobStatus(queueId, onProgress = null, pollInterval = 1000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const maxPollTime = 1800000; // Max 30 minutes for video exports
      
      const poll = async () => {
        try {
          const job = await this.getJobStatus(queueId);
          
          // Call progress callback
          if (onProgress) {
            onProgress(job.progress || 0, job.status, job);
          }

          // Check if job is complete
          if (job.status === 'completed') {
            resolve(job);
            return;
          }

          // Check if job failed
          if (job.status === 'failed') {
            reject(new Error(job.error_message || 'Video export failed'));
            return;
          }

          // Check timeout
          if (Date.now() - startTime > maxPollTime) {
            reject(new Error('Video export timeout after 30 minutes - please try again'));
            return;
          }

          // Continue polling
          setTimeout(poll, pollInterval);
          
        } catch (error) {
          reject(error);
        }
      };

      // Start polling
      poll();
    });
  }

  /**
   * Cancel a job (delete from queue)
   * @param {number} queueId - Queue job ID
   * @returns {Promise<boolean>} Success status
   */
  async cancelJob(queueId) {
    try {
      const response = await fetch(`${this.baseUrl}/queue/${queueId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        this.activeJobs.delete(queueId);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error canceling job:', error);
      return false;
    }
  }

  /**
   * Get all active jobs for monitoring
   * @returns {Array} Active jobs
   */
  getActiveJobs() {
    return Array.from(this.activeJobs.values());
  }

  /**
   * Clear completed jobs from local cache
   */
  clearCompletedJobs() {
    for (const [jobId, job] of this.activeJobs.entries()) {
      if (job.status === 'completed' || job.status === 'failed') {
        this.activeJobs.delete(jobId);
      }
    }
  }

  /**
   * Get queue jobs for a specific user with optional filtering
   * @param {string} user_id - User ID
   * @param {number} limit - Number of jobs to return (default: 10)
   * @param {string} template_id - Filter by template ID (optional)
   * @param {string} sample_type - Filter by sample type (optional)
   * @returns {Promise<Object>} User's queue jobs
   */
  async getUserQueueJobs(user_id, limit = 10, template_id = null, sample_type = null) {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (template_id) params.append('template_id', template_id);
      if (sample_type) params.append('sample_type', sample_type);

      const response = await fetch(`${this.baseUrl}/queue/user/${user_id}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting user queue jobs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get queue jobs for a specific template
   * @param {string} template_id - Template ID
   * @param {string} sample_type - Filter by sample type (optional)
   * @param {number} limit - Number of jobs to return (default: 50)
   * @returns {Promise<Object>} Template's queue jobs
   */
  async getTemplateQueueJobs(template_id, sample_type = null, limit = 50) {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (sample_type) params.append('sample_type', sample_type);

      const response = await fetch(`${this.baseUrl}/queue/template/${template_id}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting template queue jobs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const videoExportService = new VideoExportService();

export default videoExportService;
